import React, { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from 'styled-components';
import { useUser } from '../../../../../../utils/hooks/auth';
import { DeliveryListFormValues } from '../../types';
import { InputField, KeyboardDateField } from '../../../../../../components/forms/fields';
import { FullScreenDialog } from '../../../../../../ui-kit';
import DeliveryListCreateSupplierForm from '../../../DeliveryListCreateSupplierForm';
import { DeliveryListSuppliersQuery } from '../../../../../../generated/graphql';
import { getDateFormatByLocale } from '../../../../../../utils/helpers/getDateFormatByLocale';
import SupplierSearch from './SupplierSearch';
import MobileSupplierSearch from './MobileSupplierSearch';

import { SUPPLIER_ID_FIELD } from './constants';


const BasicInformation = forwardRef<HTMLDivElement>((_, ref) => {
    const [passedSearchValue, setPassedSearchValue] = useState<string | undefined>(undefined);
    const { t } = useTranslation();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isMobileSupplierSearchOpen, setIsMobileSupplierSearchOpen] = useState(false);
    const openSupplierCreateDialog = () => setIsDialogOpen(true);
    const closeSupplierCreateDialog = () => setIsDialogOpen(false);
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
    const formikProps = useFormikContext<DeliveryListFormValues>();

    const onSupplierClick = (supplier: DeliveryListSuppliersQuery['suppliers'][0]) => {
        formikProps.setFieldValue(SUPPLIER_ID_FIELD, supplier.id);
        setPassedSearchValue(supplier.legalName);
        closeMobileSupplierSearch();
    };

    const closeMobileSupplierSearch = () => setIsMobileSupplierSearchOpen(false);
    const openMobileSupplierSearch = () => {
        if (!isDesktop) {
            setIsMobileSupplierSearchOpen(true);
        }
    };

    const { user: { cultureCode } } = useUser();
    const dateFormat = getDateFormatByLocale(cultureCode);

    return (
        <div ref={ref}>
            <InputField<DeliveryListFormValues>
                name='invoiceNumber'
                label={t('Invoice Number')}
                formikProps={formikProps}
            />
            <SupplierSearch
                passedSearchValue={passedSearchValue}
                name='supplierId'
                label={t('Supplier')}
                onCreateNewClick={openSupplierCreateDialog}
                openMobilePopup={isDesktop ? undefined : openMobileSupplierSearch}
            />
            <KeyboardDateField
                name='deliveryDateUtc'
                minDate={new Date()}
                format={dateFormat}
                fullWidth
            />
            <FullScreenDialog isOpened={isDialogOpen}>
                <DeliveryListCreateSupplierForm
                    setPassedSearchValue={setPassedSearchValue}
                    onClose={closeSupplierCreateDialog}
                    dialogName={t('Create supplier')}
                />
            </FullScreenDialog>
            <FullScreenDialog isOpened={isMobileSupplierSearchOpen}>
                <MobileSupplierSearch
                    passedSearchValue={passedSearchValue}
                    onCreateNewClick={openSupplierCreateDialog}
                    onClose={closeMobileSupplierSearch}
                    onSearchResultClick={onSupplierClick}
                />
            </FullScreenDialog>
        </div>
    );
});

export default BasicInformation;
